import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Facebook, 
  Mail,
  Sun,
  Moon,
  Monitor
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSettingsStore } from '@/stores/useSettingsStore';
import { Logo } from '@/components/ui/Logo';
import { OnlineUsers } from '@/components/realtime/OnlineUsers';

const Footer = () => {
  const { settings, setTheme } = useSettingsStore();
  
  const toggleTheme = () => {
    // Cycle through all three theme options: dark → light → system → dark
    switch (settings.theme) {
      case 'dark':
        setTheme('light');
        break;
      case 'light':
        setTheme('system');
        break;
      case 'system':
        setTheme('dark');
        break;
      default:
        setTheme('dark');
    }
  };
  
  return (
    <footer className="bg-card border-t border-border">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Column 1 - About */}
          <div className="space-y-4">
            <Link to="/" className="text-2xl font-bold flex items-center space-x-2">
              <Logo />
            </Link>
            <p className="text-muted-foreground">
              Connecting holiday light enthusiasts with amazing displays and synchronized sequences.
            </p>
            <div className="flex space-x-4 pt-2">
              <SocialLink href="https://www.facebook.com/groups/632305119094352" icon={<Facebook size={18} />} />
            </div>
          </div>

          {/* Column 2 - Quick Links */}
          <div>
            <h3 className="font-medium text-lg mb-4">Quick Links</h3>
            <nav className="flex flex-col space-y-2">
              <FooterLink href="/displays" label="Displays" />
              <FooterLink href="/sequences" label="Sequences" />
              <FooterLink href="/categories" label="Categories" />
              <FooterLink href="/videos" label="Videos" />
              <FooterLink href="/about" label="About Us" />
              <FooterLink href="/contact" label="Contact" />
            </nav>
          </div>

          {/* Column 3 - Resources */}
          <div>
            <h3 className="font-medium text-lg mb-4">Resources</h3>
            <nav className="flex flex-col space-y-2">
              <FooterLink href="/getting-started" label="Getting Started" />
              <FooterLink href="/faq" label="FAQ" />
              <FooterLink href="/sequence-guide" label="Sequence Creator Guide" />
              <FooterLink href="/roadmap" label="Roadmap" />
              <FooterLink href="/feedback" label="Feedback" />
              <FooterLink href="/terms" label="Terms of Service" />
              <FooterLink href="/privacy" label="Privacy Policy" />
            </nav>
          </div>

          {/* Column 4 - Newsletter */}
          <div>
            <h3 className="font-medium text-lg mb-4">Stay Updated</h3>
            <p className="text-muted-foreground mb-4">
              Subscribe to our newsletter for the latest displays and sequences.
            </p>
            <div className="flex flex-col space-y-2">
              <Input
                type="email"
                placeholder="Enter your email"
                className="rounded-lg"
              />
              <Button className="w-full rounded-lg">
                Subscribe
              </Button>
            </div>
          </div>
        </div>

        <div className="border-t border-border mt-10 pt-6 flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Light Hunt. All rights reserved.
          </p>
          <div className="flex items-center space-x-4 mt-4 md:mt-0">
            <OnlineUsers />
            <Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground">
              Terms
            </Link>
            <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground">
              Privacy
            </Link>
            <Link to="/cookies" className="text-sm text-muted-foreground hover:text-foreground">
              Cookies
            </Link>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-offset-2"
              aria-label={`Current: ${settings.theme} theme. Click to switch to ${
                settings.theme === 'dark' ? 'light' : 
                settings.theme === 'light' ? 'system' : 'dark'
              } theme`}
              title={`Current: ${settings.theme} theme. Click to switch to ${
                settings.theme === 'dark' ? 'light' : 
                settings.theme === 'light' ? 'system' : 'dark'
              } theme`}
            >
              {settings.theme === 'dark' ? (
                <Moon size={16} className="text-muted-foreground hover:text-foreground transition-colors" />
              ) : settings.theme === 'light' ? (
                <Sun size={16} className="text-muted-foreground hover:text-foreground transition-colors" />
              ) : (
                <Monitor size={16} className="text-muted-foreground hover:text-foreground transition-colors" />
              )}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

interface SocialLinkProps {
  href: string;
  icon: React.ReactNode;
}

const SocialLink: React.FC<SocialLinkProps> = ({ href, icon }) => {
  return (
    <a 
      href={href} 
      target="_blank" 
      rel="noopener noreferrer"
      className="w-9 h-9 rounded-full flex items-center justify-center bg-muted hover:bg-muted/80 transition-colors"
    >
      {icon}
    </a>
  );
};

interface FooterLinkProps {
  href: string;
  label: string;
}

const FooterLink: React.FC<FooterLinkProps> = ({ href, label }) => {
  return (
    <Link 
      to={href} 
      className="text-muted-foreground hover:text-foreground transition-colors"
    >
      {label}
    </Link>
  );
};

export default Footer;
